/**
 * @license
 * MIT License
 * 
 * Copyright (c) 2014-present, Lee Byron and other contributors.
 * 
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 * 
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 * 
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */
import { KeyedCollection, Collection, SetCollection } from './Collection.js';
import { isOrdered } from './predicates/isOrdered.js';
import { isSet, IS_SET_SYMBOL } from './predicates/isSet.js';
import { emptyMap } from './Map.js';
import { DELETE } from './TrieUtils.js';
import { sortFactory } from './Operations.js';
import assertNotInfinite from './utils/assertNotInfinite.js';
import { asImmutable } from './methods/asImmutable.js';
import { asMutable } from './methods/asMutable.js';
import { withMutations } from './methods/withMutations.js';
import { OrderedSet } from './OrderedSet.js';
var Set = /*@__PURE__*/function (SetCollection) {
  function Set(value) {
    return value === undefined || value === null ? emptySet() : isSet(value) && !isOrdered(value) ? value : emptySet().withMutations(function (set) {
      var iter = SetCollection(value);
      assertNotInfinite(iter.size);
      iter.forEach(function (v) {
        return set.add(v);
      });
    });
  }
  if (SetCollection) Set.__proto__ = SetCollection;
  Set.prototype = Object.create(SetCollection && SetCollection.prototype);
  Set.prototype.constructor = Set;
  Set.of = function of( /*...values*/
  ) {
    return this(arguments);
  };
  Set.fromKeys = function fromKeys(value) {
    return this(KeyedCollection(value).keySeq());
  };
  Set.intersect = function intersect(sets) {
    sets = Collection(sets).toArray();
    return sets.length ? SetPrototype.intersect.apply(Set(sets.pop()), sets) : emptySet();
  };
  Set.union = function union(sets) {
    sets = Collection(sets).toArray();
    return sets.length ? SetPrototype.union.apply(Set(sets.pop()), sets) : emptySet();
  };
  Set.prototype.toString = function toString() {
    return this.__toString('Set {', '}');
  };

  // @pragma Access

  Set.prototype.has = function has(value) {
    return this._map.has(value);
  };

  // @pragma Modification

  Set.prototype.add = function add(value) {
    return updateSet(this, this._map.set(value, value));
  };
  Set.prototype.remove = function remove(value) {
    return updateSet(this, this._map.remove(value));
  };
  Set.prototype.clear = function clear() {
    return updateSet(this, this._map.clear());
  };

  // @pragma Composition

  Set.prototype.map = function map(mapper, context) {
    var this$1$1 = this;

    // keep track if the set is altered by the map function
    var didChanges = false;
    var newMap = updateSet(this, this._map.mapEntries(function (ref) {
      var v = ref[1];
      var mapped = mapper.call(context, v, v, this$1$1);
      if (mapped !== v) {
        didChanges = true;
      }
      return [mapped, mapped];
    }, context));
    return didChanges ? newMap : this;
  };
  Set.prototype.union = function union() {
    var iters = [],
      len = arguments.length;
    while (len--) iters[len] = arguments[len];
    iters = iters.filter(function (x) {
      return x.size !== 0;
    });
    if (iters.length === 0) {
      return this;
    }
    if (this.size === 0 && !this.__ownerID && iters.length === 1) {
      return this.constructor(iters[0]);
    }
    return this.withMutations(function (set) {
      for (var ii = 0; ii < iters.length; ii++) {
        if (typeof iters[ii] === 'string') {
          set.add(iters[ii]);
        } else {
          SetCollection(iters[ii]).forEach(function (value) {
            return set.add(value);
          });
        }
      }
    });
  };
  Set.prototype.intersect = function intersect() {
    var iters = [],
      len = arguments.length;
    while (len--) iters[len] = arguments[len];
    if (iters.length === 0) {
      return this;
    }
    iters = iters.map(function (iter) {
      return SetCollection(iter);
    });
    var toRemove = [];
    this.forEach(function (value) {
      if (!iters.every(function (iter) {
        return iter.includes(value);
      })) {
        toRemove.push(value);
      }
    });
    return this.withMutations(function (set) {
      toRemove.forEach(function (value) {
        set.remove(value);
      });
    });
  };
  Set.prototype.subtract = function subtract() {
    var iters = [],
      len = arguments.length;
    while (len--) iters[len] = arguments[len];
    if (iters.length === 0) {
      return this;
    }
    iters = iters.map(function (iter) {
      return SetCollection(iter);
    });
    var toRemove = [];
    this.forEach(function (value) {
      if (iters.some(function (iter) {
        return iter.includes(value);
      })) {
        toRemove.push(value);
      }
    });
    return this.withMutations(function (set) {
      toRemove.forEach(function (value) {
        set.remove(value);
      });
    });
  };
  Set.prototype.sort = function sort(comparator) {
    // Late binding
    return OrderedSet(sortFactory(this, comparator));
  };
  Set.prototype.sortBy = function sortBy(mapper, comparator) {
    // Late binding
    return OrderedSet(sortFactory(this, comparator, mapper));
  };
  Set.prototype.wasAltered = function wasAltered() {
    return this._map.wasAltered();
  };
  Set.prototype.__iterate = function __iterate(fn, reverse) {
    var this$1$1 = this;
    return this._map.__iterate(function (k) {
      return fn(k, k, this$1$1);
    }, reverse);
  };
  Set.prototype.__iterator = function __iterator(type, reverse) {
    return this._map.__iterator(type, reverse);
  };
  Set.prototype.__ensureOwner = function __ensureOwner(ownerID) {
    if (ownerID === this.__ownerID) {
      return this;
    }
    var newMap = this._map.__ensureOwner(ownerID);
    if (!ownerID) {
      if (this.size === 0) {
        return this.__empty();
      }
      this.__ownerID = ownerID;
      this._map = newMap;
      return this;
    }
    return this.__make(newMap, ownerID);
  };
  return Set;
}(SetCollection);
Set.isSet = isSet;
var SetPrototype = Set.prototype;
SetPrototype[IS_SET_SYMBOL] = true;
SetPrototype[DELETE] = SetPrototype.remove;
SetPrototype.merge = SetPrototype.concat = SetPrototype.union;
SetPrototype.withMutations = withMutations;
SetPrototype.asImmutable = asImmutable;
SetPrototype['@@transducer/init'] = SetPrototype.asMutable = asMutable;
SetPrototype['@@transducer/step'] = function (result, arr) {
  return result.add(arr);
};
SetPrototype['@@transducer/result'] = function (obj) {
  return obj.asImmutable();
};
SetPrototype.__empty = emptySet;
SetPrototype.__make = makeSet;
function updateSet(set, newMap) {
  if (set.__ownerID) {
    set.size = newMap.size;
    set._map = newMap;
    return set;
  }
  return newMap === set._map ? set : newMap.size === 0 ? set.__empty() : set.__make(newMap);
}
function makeSet(map, ownerID) {
  var set = Object.create(SetPrototype);
  set.size = map ? map.size : 0;
  set._map = map;
  set.__ownerID = ownerID;
  return set;
}
var EMPTY_SET;
function emptySet() {
  return EMPTY_SET || (EMPTY_SET = makeSet(emptyMap()));
}
export { Set };